import { notifications } from "@mantine/notifications";
import ky, { type Options } from "ky";
import {
	BadRequestException,
	ConflictException,
	InternalServerErrorException,
	NotFoundException,
	UnauthorizedException,
} from "./errors";
import { useAppState } from "./states";
import { usePortalState } from "./states";

type FetcherParams = {
	path: string;
	opts: Options;
};

export const apiFetcher = async <
	// biome-ignore lint/suspicious/noExplicitAny: default value
	T = any,
>({
	path,
	opts,
}: FetcherParams) => {
	const { faskes } = useAppState.getState();
	const { selectedTenant } = usePortalState.getState();

	if (faskes === null) throw new Error("Faskes tidak ditemukan");
	const csrf = useAppState.getState().csrf;

	if (csrf === null) throw new Error("CSRF tidak ditemukan");

	// For direct booking (non-portal), selectedTenant will be null - use faskes.tenantId instead
	// For portal booking, use selectedTenant.tenant_id
	const tenantId = selectedTenant?.tenant_id || faskes.tenantId;
	const fullUrl = `${faskes.baseApiUrl}${path}`;

	// LOG: apa yang kita kirim

	const method = opts.method ?? "GET";

	// Log request details for direct booking comparison
	if (path.includes("booking/create")) {
		if (opts.body) {
			const body =
				typeof opts.body === "string" ? JSON.parse(opts.body) : opts.body;
		}
	}

	const response = await ky(`${faskes.baseApiUrl}${path}`, {
		...opts,
		headers: {
			...opts.headers,
			"x-tenant-id": tenantId,
			"x-csrf-token": csrf,
		},
		credentials: "include",
		timeout: 5000,
		throwHttpErrors: false,
		retry: opts.retry,
	});

	type ApiResponse = {
		statusCode?: number;
		message?: string;
		data?: unknown;
		results?: unknown;
		status?: boolean;
	};
	const responseData: ApiResponse = await response.json();

	if (!response.ok) {
		const errorCode = responseData.statusCode ?? 500;
		const errorMessage = responseData.message ?? "Gagal mengambil data";
		switch (errorCode) {
			case 400:
				throw new BadRequestException(errorMessage);
			case 401:
				throw new UnauthorizedException(errorMessage);
			case 404:
				throw new NotFoundException(errorMessage);
			case 409:
				throw new ConflictException(errorMessage);
			case 500:
				throw new InternalServerErrorException(errorMessage);
			default:
				throw new Error(`${errorMessage} (Status: ${errorCode})`);
		}
	}

	// Handle both direct backend responses and portal API responses
	// Direct backend: returns data directly (null, object, array)
	// Portal API: returns {statusCode, message, results: data}
	if (
		responseData &&
		typeof responseData === "object" &&
		responseData.statusCode !== undefined &&
		responseData.results !== undefined
	) {
		// Portal API response format - extract the results field
		return responseData.results as T;
	}

	// Direct backend response or null - return as is
	return responseData as T;
};

export const dateParser = (date: string) => {
	const parts = date.split("/");
	if (parts.length === 3) {
		return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
	}
	return new Date(date);
};

export const handleDateValidation = (date: string) => {
	const parsedDate = dateParser(date);

	if (Number.isNaN(parsedDate.getTime()) || parsedDate.getFullYear() > 9999) {
		notifications.show({
			message: "Tanggal tidak valid! Format harus DD/MM/YYYY.",
			title: "Perhatian!",
			color: "red",
			withCloseButton: true,
		});
		return false;
	}

	return true;
};

export const decodeHTML = (html: string) => {
	const parser = new DOMParser();
	const decodedString =
		parser.parseFromString(html || "", "text/html").body.textContent || "";
	return decodedString;
};

// Portal API fetcher for endpoints that don't require faskes context
// biome-ignore lint/suspicious/noExplicitAny: Generic type parameter with default
export const portalApiFetcher = async <T = any>({
	path,
	opts = {},
	tenantId,
	requireAuth = false,
}: {
	path: string;
	opts?: Options;
	tenantId: string;
	requireAuth?: boolean;
}): Promise<T> => {
	const csrf = useAppState.getState().csrf;

	// Get portal state for auth handling
	// biome-ignore lint/suspicious/noExplicitAny: Dynamic import type
	let portalState: any = null;
	if (requireAuth) {
		try {
			// Dynamically import to avoid circular dependency
			const { usePortalState } = await import("~/utils/states");
			portalState = usePortalState.getState();

			// Check if token is expired and refresh if needed
			if (portalState.isTokenExpired()) {
				const refreshed = await portalState.refreshToken();
				if (!refreshed) {
					throw new UnauthorizedException(
						"Session expired. Please login again.",
					);
				}
			}
		} catch (error) {
			console.error("Portal auth check failed:", error);
			if (error instanceof UnauthorizedException) {
				throw error;
			}
		}
	}

	// Build headers
	const headers: Record<string, string> = {
		...(opts.headers as Record<string, string>),
		"x-tenant-id": tenantId,
	};

	// Add auth token if required and available
	if (requireAuth && portalState?.authToken) {
		headers.Authorization = `Bearer ${portalState.authToken}`;
	}

	// Add CSRF token if available
	if (csrf) {
		headers["x-csrf-token"] = csrf;
	}

	// With vite proxy, we can use relative paths
	// Proxy will redirect /v1/* and /api/* to backend server
	const response = await ky(path, {
		...opts,
		headers,
		credentials: "include",
		timeout: 5000,
		throwHttpErrors: false,
		retry: opts.retry,
	});

	type ApiResponse = {
		statusCode?: number;
		message?: string;
		results?: unknown;
	};
	const responseData: ApiResponse = await response.json();

	if (!response.ok) {
		const errorCode = responseData.statusCode ?? 500;
		const errorMessage = responseData.message ?? "Gagal mengambil data";

		// Handle 401 errors for authenticated requests
		if (errorCode === 401 && requireAuth && portalState) {
			// Try to refresh token once
			try {
				const refreshed = await portalState.refreshToken();
				if (refreshed) {
					// Retry the request with new token
					return portalApiFetcher({ path, opts, tenantId, requireAuth });
				}
			} catch (refreshError) {
				console.error("Token refresh failed:", refreshError);
			}

			// If refresh fails, logout user
			portalState.logout();
		}

		switch (errorCode) {
			case 400:
				throw new BadRequestException(errorMessage);
			case 401:
				throw new UnauthorizedException(errorMessage);
			case 404:
				throw new NotFoundException(errorMessage);
			case 409:
				throw new ConflictException(errorMessage);
			case 500:
				throw new InternalServerErrorException(errorMessage);
			default:
				throw new Error(`${errorMessage} (Status: ${errorCode})`);
		}
	}

	return responseData.results as T;
};
